import { useState, useEffect } from 'react'
import { Box, Grid, Stack, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { languages } from '../utils/languages'
import TitleDivider from './divider'
import { useLocaleContext } from '../context/locale'

function MoreDetails({ movie, title }) {
  const t = useTranslations('MoreDetails')
  const { country } = useLocaleContext()
  const [isLocalProduction, setIsLocalProduction] = useState(false)

  useEffect(() => {
    if (movie.primary_country && country) {
      setIsLocalProduction(movie.primary_country.toLowerCase() === country)
    } else {
      setIsLocalProduction(false)
    }
  }, [movie.primary_country, country])

  function getAge(year) {
    if (!year) return t('notAvailable')

    const currentYear = new Date().getFullYear()
    const age = currentYear - year

    if (age > 7) {
      return t('ageOver7')
    } else if (age >= 4 && age <= 7) {
      return t('age4to7')
    } else if (age >= 1 && age <= 3) {
      return t('age1to3')
    } else if (age === 0) {
      return t('lessThanYear')
    } else {
      return t('notAvailable')
    }
  }

  return (
    <Box>
      <Stack spacing={0} mt={20}>
        <TitleDivider titleText={t('moreDetailsTitle') + title.toUpperCase()} />
      </Stack>
      <Grid sx={{ fontSize: 14 }} mt={10} mb={30}>
        {/* Primera Columna */}
        <Grid.Col span={12} md={4}>
          <Stack spacing={24}>
            <TextDetails
              label={t('spokenLanguages')}
              content={
                movie.spoken_languages && movie.spoken_languages.length > 0
                  ? movie.spoken_languages.map((lang) => languages[lang]).join(', ')
                  : ''
              }
            />
            <TextDetails label={t('typeOfContent')} content={movie.type} />
            <TextDetails
              label={t('scripted')}
              content={movie.scripted === true ? t('true') : t('false')}
            />
            <TextDetails
              label={t('ageRating')}
              content={
                movie.age_ratings?.US && movie.age_ratings.US !== 'Approved'
                  ? movie.age_ratings.US
                  : t('notRated')
              }
            />
          </Stack>
        </Grid.Col>

        {/* Segunda Columna */}
        <Grid.Col span={12} md={4}>
          <Stack spacing={24}>
            <TextDetails
              label={t('releaseDate')}
              content={
                movie.release_date
                  ? new Date(movie.release_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                  : ''
              }
            />
            <TextDetails label={t('hispanic')} content={movie.hispanic} />
            <TextDetails label={t('status')} content={movie.status} />
            <TextDetails
              label={t('alternativeTitles')}
              content={
                movie.alternative_titles && movie.alternative_titles.length > 0
                  ? movie.alternative_titles
                      .map(
                        (title) =>
                          `${title.title} (${title.country} - ${title.language})`
                      )
                      .join(', ')
                  : t('notAvailable')
              }
            />
            <TextDetails
              label={t('localProduction')}
              content={isLocalProduction ? t('true') : t('false')}
            />
          </Stack>
        </Grid.Col>

        {/* Tercera Columna */}
        <Grid.Col span={12} md={4}>
          <Stack spacing={24}>
            <TextDetails
              label={t('genres')}
              content={
                movie.genres && movie.genres.length > 0
                  ? movie.genres.join(', ')
                  : ''
              }
            />
            <TextDetails
              label={t('keywords')}
              content={
                movie.keywords && movie.keywords.length > 0
                  ? movie.keywords.join(', ')
                  : ''
              }
            />
            <TextDetails
              label={t('homepage')}
              content={
                movie.homepage ? (
                  <a href={movie.homepage} target="_blank" rel="noopener noreferrer">
                    {movie.homepage}
                  </a>
                ) : (
                  ''
                )
              }
            />
            <TextDetails
              label={t('productionCompanies')}
              content={
                movie.production_companies && movie.production_companies.length > 0
                  ? movie.production_companies.join(', ')
                  : ''
              }
            />
          </Stack>
        </Grid.Col>
      </Grid>
    </Box>
  )
}

function TextDetails({ label, content }) {
  return (
    <Stack spacing={0} sx={{ minHeight: 60 }}>
      <Text c="dimmed">{label}</Text>
      <Text color="white">{content || 'No data available'}</Text>
    </Stack>
  )
}

export default MoreDetails
